import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion } from 'framer-motion';

const accessories = [
  {
    id: 'filter-cartridge',
    title: 'Filter Cartridge',
    description: 'High-quality filter cartridges for various industrial and commercial applications.',
    image: '/images/products/filter-cartridge.jpg',
    features: [
      'Multiple micron ratings',
      'Various materials available',
      'High flow capacity',
      'Extended service life',
      'Easy replacement'
    ]
  },
  {
    id: 'uf-ro-element',
    title: 'UF & RO Element',
    description: 'Premium ultrafiltration and reverse osmosis membrane elements.',
    image: '/images/products/membrane-element.jpg',
    features: [
      'High rejection rates',
      'Low fouling tendency',
      'Extended membrane life',
      'Multiple sizes available',
      'Industry-leading performance'
    ]
  },
  {
    id: 'membrane',
    title: 'Membrane',
    description: 'Advanced membrane technology for superior filtration performance.',
    image: '/images/products/membrane.jpg',
    features: [
      'High permeability',
      'Excellent selectivity',
      'Chemical resistance',
      'Thermal stability',
      'Various pore sizes'
    ]
  },
  {
    id: 'vessels',
    title: 'Vessels',
    description: 'Durable pressure vessels for membrane and filter housing.',
    image: '/images/products/vessels.jpg',
    features: [
      'Corrosion resistant',
      'Multiple pressure ratings',
      'Easy maintenance',
      'Various sizes',
      'ASME certified'
    ]
  },
  {
    id: 'instruments',
    title: 'Instruments',
    description: 'Precision instruments for monitoring and control.',
    image: '/images/products/instruments.jpg',
    features: [
      'High accuracy',
      'Digital display',
      'Easy calibration',
      'Multiple parameters',
      'Data logging capability'
    ]
  },
  {
    id: 'io-cards',
    title: 'IO Cards',
    description: 'Advanced I/O cards for system automation and control.',
    image: '/images/products/io-cards.jpg',
    features: [
      'Multiple I/O channels',
      'High reliability',
      'Easy integration',
      'Industrial grade',
      'Modular design'
    ]
  },
  {
    id: 'plc-controller',
    title: 'PLC Controller',
    description: 'State-of-the-art PLC controllers for automated system management.',
    image: '/images/products/plc-controller.jpg',
    features: [
      'Advanced programming',
      'Remote monitoring',
      'Expandable I/O',
      'Network connectivity',
      'Touch interface'
    ]
  },
  {
    id: 'dosing-pumps',
    title: 'Dosing Pumps',
    description: 'Precise chemical dosing pumps for water treatment applications.',
    image: '/images/products/dosing-pumps.jpg',
    features: [
      'Accurate dosing',
      'Multiple flow rates',
      'Chemical resistant',
      'Easy maintenance',
      'Digital control'
    ]
  },
  {
    id: 'chemical-tanks',
    title: 'Chemical Tanks',
    description: 'Chemical storage tanks designed for safety and durability.',
    image: '/images/products/chemical-tanks.jpg',
    features: [
      'Chemical resistant',
      'UV protected',
      'Multiple capacities',
      'Safety features',
      'Easy installation'
    ]
  }
];

const Accessories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Box sx={{ bgcolor: '#f8f9fa' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '300px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.45))',
            zIndex: 1
          }
        }}
      >
        <Box
          component="img"
          src="/images/products/accessories-hero.jpg"
          alt="Water Treatment Accessories"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: { xs: '2.5rem', md: '4rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 2
            }}
          >
            Accessories
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '800px',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            High-quality components and accessories for water treatment systems
          </Typography>
        </Container>
      </Box>

      {/* Products Grid */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        <Grid container spacing={4}>
          {accessories.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card
                  onMouseEnter={() => setHoveredCard(item.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', paddingTop: '60%' }}>
                    <CardMedia
                      component="img"
                      image={item.image}
                      alt={item.title}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease',
                        ...(hoveredCard === item.id && {
                          transform: 'scale(1.05)'
                        })
                      }}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      gutterBottom
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'primary.main'
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {item.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {item.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(0, 127, 255, 0.1)',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'rgba(0, 127, 255, 0.2)'
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Accessories; 